/**
 * IP-restriction detection (single source of truth).
 *
 * An org can restrict the portal to a set of IP ranges (PLO-4976). When the
 * caller is outside them the API halts every request in the member pipeline
 * with:
 *
 *   403 { error: "Forbidden", code: "ip_restricted", message: "..." }
 *
 * Every query then fails, and before PLO-5010 the portal reacted by throwing
 * the first failure to the route error boundary — so a blocked employee saw
 * the generic "Something went wrong" page and had no idea they were simply on
 * the wrong network.
 *
 * Match on `code`, never on the message or on the bare 403: a 403 is also how
 * an ordinary permission failure is reported, and blanking the whole portal on
 * one of those would be a bug. The string is a contract with
 * `XWeb.Plugs.RestrictIpAccess` on the server.
 *
 * Detection is deliberately a side-effecting flag rather than per-query error
 * handling: the block is a property of the network, not of one request, so the
 * first request to hit it should take the entire app down to one screen.
 */
import { create } from "zustand";

export const IP_RESTRICTED_CODE = "ip_restricted";

export const isIpRestrictedBody = (status: number, body: unknown): boolean => {
  if (status !== 403 || typeof body !== "object" || body === null) return false;
  return (body as { code?: unknown }).code === IP_RESTRICTED_CODE;
};

type IpRestrictionStore = {
  /** True once any request has come back IP-blocked. Never flips back — the
   *  screen offers a reload, which starts a fresh page load. */
  isIpRestricted: boolean;
};

export const useIpRestrictionStore = create<IpRestrictionStore>()(() => ({
  isIpRestricted: false,
}));

export const markIpRestricted = () => {
  if (!useIpRestrictionStore.getState().isIpRestricted) {
    useIpRestrictionStore.setState({ isIpRestricted: true });
  }
};

/**
 * Reads a fetch Response as JSON, flagging an IP block on the way past.
 *
 * Wraps the `res.json()` step of all four `membersApi` methods, so nothing
 * routed through the API layer can forget it. A handful of authed fetches
 * still bypass `membersApi` (`state/members/accessReviews.ts`,
 * `accessReviewsEvidence.ts`); they throw on `!res.ok` before ever parsing a
 * body, so wiring them up means more than swapping this in. Not worth it while
 * TopNav's `useGetMe`/`useGetPortalConfig`/`useGetStats` mount on every route
 * and raise the flag through here regardless of which page was deep-linked.
 *
 * Parse failures propagate exactly as they did before this existed.
 */
export const readBody = async <T>(res: Response): Promise<T> => {
  const body = await res.json();
  if (isIpRestrictedBody(res.status, body)) markIpRestricted();
  return body as T;
};
